// Public read-API client for next.actianlabs.com.
// Backend lives at /api/v1/* (proxied to :8086 in dev, same-origin in prod).

import { fetchWithAuth } from './auth.js'
import { conferenceLabel } from './text.js'

const BASE = '/api/v1'

// Human-readable sentence for an HTTP status. Used everywhere instead of
// leaking bare "HTTP 502" strings into ErrorAlert.
function friendlyStatusMessage(status) {
  if (status === 401) return 'Your session has expired — please sign in again.'
  if (status === 403) return "You don't have permission to do that."
  if (status === 404) return "That wasn't found — it may have been moved or removed."
  if (status === 409) return 'That conflicts with existing data — reload and try again.'
  if (status === 429) return 'Too many requests — wait a moment and try again.'
  if (status === 502 || status === 503 || status === 504) {
    return 'The server is temporarily unavailable — it may be restarting. Try again in a moment.'
  }
  if (status >= 500) return 'Something went wrong on the server. Try again in a moment.'
  return `The request failed (HTTP ${status}).`
}

// Maps a non-OK Response to a human Error. Prefers the server's JSON `message`
// (Spring's error envelope — server.error.include-message=always); otherwise a
// status-specific sentence. Always sets `.status` and `.body`. Reads the body
// once — do not also call res.text() at the call site.
async function httpError(res) {
  const text = await res.text().catch(() => '')
  let serverMsg = ''
  if (text) {
    try {
      const body = JSON.parse(text)
      if (body && typeof body.message === 'string') serverMsg = body.message.trim()
    } catch { /* not JSON — leave serverMsg empty */ }
  }
  const err = new Error(serverMsg || friendlyStatusMessage(res.status))
  err.status = res.status
  err.body = text
  return err
}

// Transient 5xx (502/503/504) almost always means the backend is mid-restart
// during a deploy. Retry the (idempotent) GET a few times with short backoff so
// a deploy window never surfaces to the user as an error.
const TRANSIENT_STATUS = new Set([502, 503, 504])

async function getWithRetry(doFetch, attempts = 3) {
  for (let attempt = 1; ; attempt++) {
    const res = await doFetch()
    if (res.ok || !TRANSIENT_STATUS.has(res.status) || attempt >= attempts) return res
    await new Promise((resolve) => setTimeout(resolve, 300 * attempt))
  }
}

// All authenticated API calls go through this. GETs are retried on a transient
// 5xx (see getWithRetry); non-GET calls are passed straight through — they are
// not safe to auto-retry.
async function apiFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  if (method !== 'GET') return fetchWithAuth(url, options)
  return getWithRetry(() => fetchWithAuth(url, options))
}

async function getJson(path, params) {
  const url = new URL(path, window.location.origin)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue
      url.searchParams.set(k, String(v))
    }
  }
  const res = await getWithRetry(() =>
    fetch(url.pathname + url.search, { headers: { Accept: 'application/json' } }))
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export function listEvents(params = {}) {
  return getJson(`${BASE}/events`, params)
}

// #307/#308: conference reads. getConference returns the container with its
// PUBLISHED talks nested — a private conference legitimately comes back with an
// empty `talks` array rather than a 404.
export function listConferences(params = {}) {
  return getJson(`${BASE}/conferences`, params)
}

export function getConference(id) {
  return getJson(`${BASE}/conferences/${encodeURIComponent(id)}`)
}

export function listSpeakers() {
  return getJson(`${BASE}/speakers`)
}

export function listTags() {
  return getJson(`${BASE}/tags`)
}

// POST /api/v1/me/request-speaker-promotion (#269). Triggered when a VIEWER
// lands on /admin — server idempotently stamps the request timestamp and
// emails every current admin asking them to promote the user. Response:
// { requestedAt: ISO8601, newRequest: bool, adminsNotified: int }. Safe to
// call on every mount — server de-dupes within a configurable window.
export async function requestSpeakerPromotion() {
  const res = await apiFetch(`${BASE}/me/request-speaker-promotion`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

// POST /api/v1/tags — self-serve tag creation (#116). Authenticated only;
// idempotent on slug collision (server returns the existing row with 200).
export async function createTag(name) {
  const res = await apiFetch(`${BASE}/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ name }),
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

// Admin tag management (#294 backend). All ROLE_ADMIN-gated under
// /api/v1/admin/tags. rename keeps the slug stable; delete soft-deletes and
// unlinks from events; merge folds source into target and returns the target.
export async function renameTag(id, name) {
  const res = await apiFetch(`${BASE}/admin/tags/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ name }),
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export async function deleteTag(id) {
  const res = await apiFetch(`${BASE}/admin/tags/${id}`, { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw await httpError(res)
}

export async function mergeTag(sourceId, targetId) {
  const res = await apiFetch(`${BASE}/admin/tags/${sourceId}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ targetId }),
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export function icsUrl(eventId) {
  return `${BASE}/events/${eventId}.ics`
}

// "Share on LinkedIn" — uses LinkedIn's no-auth share-offsite endpoint with a
// deep link to the event detail page (#39) AND a pre-filled blurb (#42)
// mentioning the event, speakers, date, and location. LinkedIn's share dialog
// honors `text` when present; if it's ever dropped client-side, the URL alone
// still works as before (purely additive).
//
// Per-event Open Graph cards is a follow-up: the SPA still serves the same
// OG meta on every route until SSR / pre-rendering ships.
export function linkedInShareUrl(event) {
  if (!event?.id || typeof window === 'undefined') return null
  const target = `${window.location.origin}/events/${encodeURIComponent(event.id)}`
  const text = linkedInShareText(event, target)
  const params = new URLSearchParams({ url: target })
  if (text) params.set('text', text)
  return `https://www.linkedin.com/sharing/share-offsite/?${params.toString()}`
}

// Build the suggested post body. Pure function — takes the resolved share URL
// as the second arg so tests can pin it deterministically; production callers
// let `linkedInShareUrl` provide it. Returns null when there's nothing
// meaningful to say (no title + no conference).
export function linkedInShareText(event, shareUrl) {
  if (!event) return null
  // #341: `event.conference` is a nested OBJECT since #307, so calling a string
  // method on it throws — and this runs inside EventActions' render, which took
  // the whole detail page down with it (white screen on every talk attached to
  // a conference). `conferenceLabel` resolves either shape.
  const headline = (conferenceLabel(event) || event.title || '').trim()
  if (!headline) return null

  const speakers = event.speakers || []
  const main = speakers.find((s) => s.isMain)
  const co = speakers.filter((s) => !s.isMain)
  const speakerName = (s) => (s?.displayName || (s?.userId ? `User #${s.userId}` : null))

  const mainName = speakerName(main)
  const coNames = co.map(speakerName).filter(Boolean)
  let withClause = ''
  if (mainName && coNames.length === 0) {
    withClause = ` with ${mainName}`
  } else if (mainName && coNames.length === 1) {
    withClause = ` with ${mainName} and ${coNames[0]}`
  } else if (mainName && coNames.length > 1) {
    withClause = ` with ${mainName} and ${coNames.slice(0, -1).join(', ')}, and ${coNames[coNames.length - 1]}`
  }

  let dateClause = ''
  if (event.startsAtUtc) {
    const fmt = new Intl.DateTimeFormat(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      ...(event.tzId ? { timeZone: event.tzId } : {}),
    })
    dateClause = ` on ${fmt.format(new Date(event.startsAtUtc))}`
  }

  const placeBits = [event.city, event.country].filter(Boolean)
  const placeClause = placeBits.length ? ` — ${placeBits.join(', ')}` : ''

  const sentence = `See you at ${headline}${withClause}${dateClause}${placeClause}.`
  return shareUrl ? `${sentence} ${shareUrl}` : sentence
}

// "Share on Twitter / X" — uses Twitter's web intent endpoint (`/intent/tweet`)
// with a pre-filled tweet body. Twitter still honors `twitter.com/intent/tweet`
// even after the X rebrand; it 302s to x.com when the user clicks. Reuses the
// same blurb as the LinkedIn share so the message is consistent.
export function twitterShareUrl(event) {
  if (!event?.id || typeof window === 'undefined') return null
  const target = `${window.location.origin}/events/${encodeURIComponent(event.id)}`
  const text = linkedInShareText(event, null) // headline + speakers + date + place, no URL
  const params = new URLSearchParams({ url: target })
  if (text) params.set('text', text)
  return `https://twitter.com/intent/tweet?${params.toString()}`
}

// "Share on Substack" — opens Substack's Notes compose flow with the share text
// pre-filled. Substack doesn't publish a stable share-offsite endpoint the way
// LinkedIn does, but `substack.com/notes?action=compose&content=...` is the URL
// pattern Substack itself produces from its in-app share buttons (verified
// 2026-05). If they ever change it, the worst case is an empty Notes compose
// page — the user can still paste from clipboard.
export function substackShareUrl(event) {
  if (!event?.id || typeof window === 'undefined') return null
  const target = `${window.location.origin}/events/${encodeURIComponent(event.id)}`
  const text = linkedInShareText(event, target) // sentence + URL appended
  const params = new URLSearchParams({ action: 'compose' })
  if (text) params.set('content', text)
  return `https://substack.com/notes?${params.toString()}`
}

// Copy the share text to the clipboard and return the Substack compose URL.
// Does NOT open the URL — the caller (SubstackShareButton) shows a paste
// notification first, then opens after a brief delay so the user reads the
// "Copied — paste into Substack" hint before being redirected. On iOS the
// hint matters most: Universal Links hijack substack.com/notes and drop the
// compose params, so the user lands on the Notes feed and needs to paste
// manually. See #66, #80.
//
// Returns { url, copied }. `url` is null when the event has no id; `copied`
// is true when clipboard.writeText resolved, false when clipboard is
// unavailable or rejects.
export async function copySubstackShareText(event) {
  const url = substackShareUrl(event)
  if (!url || typeof window === 'undefined') return { url: null, copied: false }
  const target = `${window.location.origin}/events/${encodeURIComponent(event.id)}`
  const text = linkedInShareText(event, target)
  let copied = false
  if (text && navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      copied = true
    } catch { /* silent — caller still shows the toast and opens the tab */ }
  }
  return { url, copied }
}

// Build a "Add to Google Calendar" URL. Google's render endpoint accepts
// dates as YYYYMMDDTHHmmssZ (UTC). Spec: https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/google.md
export function googleCalendarUrl(event) {
  // #350: `new Date(undefined).toISOString()` throws a RangeError, and this now
  // runs for EVERY event in a feed — including each talk nested in a conference
  // — instead of only for a standalone card. One event missing `endsAtUtc` used
  // to be survivable; today it would take the whole list down (the #341 failure
  // mode). Return null for an unusable date and simply omit `dates`: Google's
  // template opens on today with the title pre-filled, which beats a blank page.
  const fmt = (iso) => {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')
  }
  const start = fmt(event.startsAtUtc)
  // A start with no end gets a one-hour default so the entry still lands on
  // the right day at the right time. Every published event has an end today —
  // this is belt-and-braces for an unsaved draft or a future nullable column.
  const end = fmt(event.endsAtUtc) || (start ? fmt(new Date(new Date(event.startsAtUtc).getTime() + 3600000).toISOString()) : null)
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || '',
    details: [event.abstractText, event.registrationUrl].filter(Boolean).join('\n\n'),
    location: [event.venue, event.city, event.region, event.country].filter(Boolean).join(', '),
    ctz: event.tzId || 'UTC',
  })
  if (start && end) params.set('dates', `${start}/${end}`)
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

// #348: handing a published contract to the Data Product Workbench.
//
// The Workbench is a separate application on a separate host with its own
// identity. next.actianlabs.com does not know who the Workbench user is and
// must not try to find out — so the deep link carries a URL and nothing else.
// The Workbench signs the user in, fetches the document through api.jgp.ai
// (server-side, which is also why this needs no CORS negotiation), creates it
// with NO `org` — that is what puts it in the personal workspace — and opens
// its editor.
//
// The official WB2 host, deliberately, not the workbench-dev preview.
export const WORKBENCH_ORIGIN = 'https://workbench.actianlabs.com'

// ON since 2026-08-21. Both dependencies shipped and were verified against the
// live systems, not against their issue state:
//
//   jgperrin/ai.jgp.bitol.svc#1180        — `odcsUrl` is a parameter on
//     POST /v4/contracts in the OpenAPI spec regenerated from the live 9.34.0
//     gateway, so svc will fetch an ODCS document server-side.
//   jgperrin/ai.jgp.workbench.webapp#1307 — `importOdcsUrl` is present in the
//     live bundle served by workbench.actianlabs.com, and `/editor` routes to
//     OdcsUrlImportPage when the parameter is set.
//
// The remaining precondition is on our side and holds: all four contract URLs
// answer 200 application/yaml to an unauthenticated request, which is what svc
// fetches. If any of those three facts stops being true, set this back to false
// rather than leaving a button that lands on an empty editor.
export const WORKBENCH_IMPORT_ENABLED = true

// Absolute, percent-encoded deep link for one contract, or null when there is
// nothing sane to link to — no contract, no download URL, or no `window` (SSR),
// in which case the caller renders no action rather than a dead link.
export function workbenchImportUrl(contract) {
  if (!contract?.downloadUrl || typeof window === 'undefined') return null
  const target = new URL(contract.downloadUrl, window.location.origin).toString()
  return `${WORKBENCH_ORIGIN}/editor?importOdcsUrl=${encodeURIComponent(target)}`
}

// ODCS data-contract endpoints (#46, #197). Each contract describes one
// dataset of next.actianlabs.com. The `inlineUrl` flavor is for in-page
// rendering; `downloadUrl` (.odcs.yaml) is the Content-Disposition attachment.
// The /contract page renders these as tabs.
export const CONTRACTS = [
  {
    key: 'events',
    label: 'Events',
    inlineUrl: `${BASE}/contracts/events`,
    downloadUrl: `${BASE}/contracts/events.odcs.yaml`,
    blurb: 'The public, read-only events dataset served by /api/v1/events.',
  },
  {
    // #345: served by svc since #307, but this registry was never updated, so
    // the contract was published and invisible for its whole existence. It
    // sits second because events and conferences are the two halves of one
    // model — talks and the containers that hold them — while users and
    // bookings are a different concern entirely.
    key: 'conferences',
    label: 'Conferences',
    inlineUrl: `${BASE}/contracts/conferences`,
    downloadUrl: `${BASE}/contracts/conferences.odcs.yaml`,
    blurb: 'The containers that hold talks — dates, venue and registration — served by /api/v1/conferences.',
  },
  {
    key: 'users',
    label: 'Users',
    inlineUrl: `${BASE}/contracts/users`,
    downloadUrl: `${BASE}/contracts/users.odcs.yaml`,
    blurb: 'The membership & identity model behind the actianlabs-next Workbench group.',
  },
  {
    key: 'bookings',
    label: 'Bookings',
    inlineUrl: `${BASE}/contracts/bookings`,
    downloadUrl: `${BASE}/contracts/bookings.odcs.yaml`,
    blurb: 'The inbound speaking-request pipeline behind the public booking form.',
  },
]

// Fetches one contract's raw ODCS YAML. Pass a CONTRACTS[].inlineUrl.
export async function fetchContract(inlineUrl) {
  const res = await getWithRetry(() =>
    fetch(inlineUrl, { headers: { Accept: 'application/yaml' } }))
  if (!res.ok) throw await httpError(res)
  return res.text()
}

// Booking endpoints (issue #20).
//
// POST /api/v1/bookings is public — anyone can request a speaker.
// /api/v1/admin/bookings is ADMIN-only — fetchWithAuth attaches the bearer.
export async function createBooking(payload) {
  const res = await fetch(`${BASE}/bookings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const err = new Error(
      res.status === 400 ? 'Booking rejected — check the form fields.' :
      friendlyStatusMessage(res.status)
    )
    err.status = res.status
    err.body = text
    throw err
  }
  return res.json()
}

// MCP device-code confirm (#82, repathed to /api/v1/auth in #152). The /link
// page calls this with the user code from the URL bar; bearer is attached
// automatically. Throws an Error with `status` AND `kind` set so the caller
// can render an ErrorAlert + a per-kind RecoveryBlock with tailored copy.
//
// Kind map (#152):
//   401 → 'session-expired'    — bearer expired between page load and submit
//   404 → 'code-not-found'     — wrong code or already cleared
//   410 → 'code-expired'       — code TTL elapsed
//   409 → 'already-confirmed'  — someone else confirmed first
//   5xx → 'server-error'       — upstream / db / config blew up
//   *   → 'unknown'            — fall-through for any other 4xx
export async function confirmDeviceCode(userCode) {
  const res = await apiFetch('/api/v1/auth/device-code/confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ userCode }),
  })
  if (!res.ok) {
    let msg = `Confirmation failed (HTTP ${res.status}).`
    try {
      const body = await res.json()
      if (body && typeof body.error === 'string') msg = body.error
    } catch {
      // fall through to the generic message
    }
    const err = new Error(msg)
    err.status = res.status
    err.kind = kindForStatus(res.status)
    throw err
  }
  return res.json()
}

function kindForStatus(status) {
  if (status === 401) return 'session-expired'
  if (status === 404) return 'code-not-found'
  if (status === 410) return 'code-expired'
  if (status === 409) return 'already-confirmed'
  if (status >= 500) return 'server-error'
  return 'unknown'
}

// Speaker self-service for bookings (#44 dashboard). Returns the bookings the
// caller is attached to as a speaker, newest-first. The endpoint already
// filters server-side; client just renders.
export async function listMyBookings() {
  const res = await apiFetch(`${BASE}/me/bookings`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export async function listAdminBookings(params = {}) {
  const url = new URL(`${BASE}/admin/bookings`, window.location.origin)
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    url.searchParams.set(k, String(v))
  }
  const res = await apiFetch(url.pathname + url.search, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export async function patchAdminBooking(id, patch) {
  const res = await apiFetch(`${BASE}/admin/bookings/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

// Speaker event self-service (#36).
//
// listMyEvents() returns every event where the caller is the main speaker
// or attached as a co-speaker — including DRAFT and CANCELLED, which are
// hidden from the public /api/v1/events list.
export async function listMyEvents() {
  const res = await apiFetch(`${BASE}/me/events`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export async function getMyEvent(id) {
  const res = await apiFetch(`${BASE}/me/events/${id}`, {
    headers: { Accept: 'application/json' },
  })
  // The /me/events endpoint doesn't return single events directly; the page
  // calls listMyEvents() and finds-by-id. This helper is here for symmetry
  // with future admin paths (#37) that may add GET /admin/events/{id}.
  if (res.status === 404) return null
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export async function createMyEvent(payload) {
  return upsertEvent(`${BASE}/me/events`, 'POST', payload)
}

export async function patchMyEvent(id, payload) {
  return upsertEvent(`${BASE}/me/events/${id}`, 'PATCH', payload)
}

export async function deleteMyEvent(id) {
  const res = await apiFetch(`${BASE}/me/events/${id}`, { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw await httpError(res)
}

async function upsertEvent(url, method, payload) {
  const res = await apiFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    // Spring's error envelope is JSON with `message` once
    // server.error.include-message=always is set (#88). Prefer the parsed
    // message; fall back to raw text for non-JSON or no-message responses.
    let parsedMessage = ''
    if (text) {
      try {
        const body = JSON.parse(text)
        if (body && typeof body.message === 'string' && body.message.trim()) {
          parsedMessage = body.message.trim()
        }
      } catch { /* not JSON — fall through */ }
    }
    const err = new Error(
      res.status === 400 ? (parsedMessage || text || 'Validation failed.') :
      res.status === 403 ? 'Not allowed — only the main speaker (or an admin) can edit.' :
      (parsedMessage || friendlyStatusMessage(res.status))
    )
    err.status = res.status
    err.body = text
    throw err
  }
  return res.json()
}

// Admin event management (#37). Same shape as me/events but unrestricted by
// ownership, with extra filters (status, speaker_id, q, includeDeleted).
export async function listAdminEvents(params = {}) {
  const url = new URL(`${BASE}/admin/events`, window.location.origin)
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    url.searchParams.set(k, String(v))
  }
  const res = await apiFetch(url.pathname + url.search, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export async function createAdminEvent(payload) {
  return upsertEvent(`${BASE}/admin/events`, 'POST', payload)
}

export async function patchAdminEvent(id, payload) {
  return upsertEvent(`${BASE}/admin/events/${id}`, 'PATCH', payload)
}

export async function deleteAdminEvent(id) {
  const res = await apiFetch(`${BASE}/admin/events/${id}`, { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw await httpError(res)
}

export async function restoreAdminEvent(id) {
  const res = await apiFetch(`${BASE}/admin/events/${id}/restore`, { method: 'POST' })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

// Admin user / role management (#35).
export async function listAdminUsers({ includeDeleted = false } = {}) {
  const url = new URL(`${BASE}/admin/users`, window.location.origin)
  if (includeDeleted) url.searchParams.set('includeDeleted', 'true')
  const res = await apiFetch(url.pathname + url.search, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export async function createAdminUser(payload) {
  return upsertUser(`${BASE}/admin/users`, 'POST', payload)
}

export async function patchAdminUser(userId, payload) {
  return upsertUser(`${BASE}/admin/users/${userId}`, 'PATCH', payload)
}

export async function deleteAdminUser(userId) {
  const res = await apiFetch(`${BASE}/admin/users/${userId}`, { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw await httpError(res)
}

export async function restoreAdminUser(userId) {
  const res = await apiFetch(`${BASE}/admin/users/${userId}/restore`, { method: 'POST' })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

// #379: this used to hand-roll its own non-OK branch, and its 400 case was
// `text || 'Validation failed.'` — the whole Spring error envelope, rendered
// verbatim in the red alert. `httpError` already pulls `body.message` out
// (server.error.include-message=always shipped with #88) and every other
// admin call goes through it, so this was the last place a user could be
// shown raw JSON. The 409 wording is app-specific and stays.
async function upsertUser(url, method, payload) {
  const res = await apiFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const err = await httpError(res)
    if (res.status === 409) err.message = 'A user with that email or user_id already exists.'
    throw err
  }
  return res.json()
}

// --- AMA announcements (#377) ------------------------------------------------
//
// The back-office surface for the LinkedIn messages attached to each event
// (svc #376). Everything here is ROLE_ADMIN and goes through fetchWithAuth.

export async function listEventMessages(params = {}) {
  const url = new URL(`${BASE}/admin/event-messages`, window.location.origin)
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    url.searchParams.set(k, String(v))
  }
  const res = await apiFetch(url.pathname + url.search, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

export async function getEventMessagesConfig() {
  const res = await apiFetch(`${BASE}/admin/event-messages/config`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

async function messageWrite(path, method, payload) {
  const res = await apiFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  })
  if (!res.ok) throw await httpError(res)
  return res.status === 204 ? null : res.json()
}

export function createEventMessage(payload) {
  return messageWrite(`${BASE}/admin/event-messages`, 'POST', payload)
}

export function patchEventMessage(id, payload) {
  return messageWrite(`${BASE}/admin/event-messages/${id}`, 'PATCH', payload)
}

// Sends to a real public feed. The page confirms before calling this.
export function publishEventMessage(id) {
  return messageWrite(`${BASE}/admin/event-messages/${id}/publish`, 'POST')
}

export function skipEventMessage(id) {
  return messageWrite(`${BASE}/admin/event-messages/${id}/skip`, 'POST')
}

export async function deleteEventMessage(id) {
  const res = await apiFetch(`${BASE}/admin/event-messages/${id}`, { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw await httpError(res)
}

export async function uploadEventImage(file, { eventId, altText } = {}) {
  const url = new URL(`${BASE}/admin/event-images`, window.location.origin)
  if (eventId) url.searchParams.set('eventId', eventId)
  if (altText) url.searchParams.set('altText', altText)
  const fd = new FormData()
  fd.append('file', file)
  // No Content-Type header: the browser must set the multipart boundary.
  const res = await apiFetch(url.pathname + url.search, { method: 'POST', body: fd })
  if (!res.ok) throw await httpError(res)
  return res.json()
}

// The bytes endpoint is admin-only, so a bare <img src> would go without the
// bearer token and 401. Fetch it through the authenticated client and hand back
// an object URL instead — callers must URL.revokeObjectURL it when done.
export async function fetchEventImageObjectUrl(id) {
  const res = await apiFetch(`${BASE}/admin/event-images/${id}/bytes`)
  if (!res.ok) throw await httpError(res)
  return URL.createObjectURL(await res.blob())
}
